import { useMemo, useState } from 'react'
import { createFileRoute } from '@tanstack/react-router'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { z } from 'zod'
import { cn } from '@/lib/cn'
import type { FeatureFlagDto } from '@/api/types'
import {
  flagsQuery,
  useRemoveFlagOverride,
  useSetFlag,
  useSetFlagCategory,
} from '@/features/platform/platform.queries'
import { FlagOverrideDialog } from '@/features/platform/FlagOverrideDialog'
import {
  Badge,
  Button,
  Card,
  CardHeader,
  ConfirmDialog,
  EmptyState,
  ErrorState,
  LoadingState,
  PageHeader,
  SearchInput,
  Switch,
  useToast,
} from '@/ui'
import { IconAdd, IconClose, IconExpandMore } from '@/icons'

const searchSchema = z.object({ q: z.string().default('') })

export const Route = createFileRoute('/_authed/flags/')({
  validateSearch: searchSchema,
  component: FlagsPage,
})

function FlagsPage() {
  const { t } = useTranslation()
  const search = Route.useSearch()
  const navigate = Route.useNavigate()
  const toast = useToast()

  const query = useQuery(flagsQuery)
  const setFlag = useSetFlag()
  const setCategory = useSetFlagCategory()
  const removeOverride = useRemoveFlagOverride()

  const [confirmingOff, setConfirmingOff] = useState<FeatureFlagDto | null>(null)
  const [overriding, setOverriding] = useState<FeatureFlagDto | null>(null)

  /*
   * Collapsed until asked for. Six categories of switches expanded at once is a wall nobody
   * reads; an operator arrives knowing which area they came to change.
   *
   * A Set of the OPEN keys rather than the closed ones, so a category the backend adds later
   * starts collapsed like the rest instead of appearing open.
   */
  const [openCategories, setOpenCategories] = useState<ReadonlySet<string>>(new Set())

  function toggleCategory(key: string) {
    setOpenCategories((current) => {
      const next = new Set(current)
      if (next.has(key)) next.delete(key)
      else next.add(key)

      return next
    })
  }

  const grouped = useMemo(() => {
    const flags = query.data?.items ?? []
    const term = search.q.trim().toLowerCase()

    const matching = term
      ? flags.filter(
          (flag) =>
            flag.key.toLowerCase().includes(term) ||
            flag.description.toLowerCase().includes(term),
        )
      : flags

    const byCategory = new Map<string, { name: string; flags: FeatureFlagDto[] }>()
    for (const flag of matching) {
      const entry = byCategory.get(flag.categoryKey) ?? { name: flag.categoryName, flags: [] }
      entry.flags.push(flag)
      byCategory.set(flag.categoryKey, entry)
    }

    return [...byCategory.entries()]
  }, [query.data, search.q])

  // While searching, every category with a hit is open — a filtered list that hides its own
  // results is worse than no search at all.
  const isSearching = search.q.trim().length > 0

  function apply(flag: FeatureFlagDto, isEnabled: boolean) {
    setFlag.mutate(
      { key: flag.key, isEnabled },
      {
        onSuccess: () => {
          toast.success(t('flags.updated'))
          setConfirmingOff(null)
        },
        onError: (error) => {
          toast.fromError(error, t('state.errorTitle'))
        },
      },
    )
  }

  if (query.isPending) return <LoadingState />
  if (query.isError) {
    return (
      <ErrorState
        error={query.error}
        onRetry={() => {
          void query.refetch()
        }}
      />
    )
  }

  return (
    <>
      <PageHeader title={t('flags.title')} description={t('flags.subtitle')} />

      <SearchInput
        value={search.q}
        onChange={(q) => {
          void navigate({ search: { q } })
        }}
        placeholder={t('flags.search')}
        className="mb-4 max-w-sm"
      />

      {grouped.length === 0 ? (
        <Card>
          <EmptyState
            title={search.q ? t('state.emptyFiltered') : t('flags.empty')}
            {...(search.q ? { description: t('state.emptyFilteredHint') } : {})}
          />
        </Card>
      ) : (
        <div className="flex flex-col gap-4">
          {grouped.map(([categoryKey, category]) => {
            const allOn = category.flags.every((flag) => flag.isEnabled)
            const isOpen = isSearching || openCategories.has(categoryKey)

            return (
              <Card key={categoryKey} isFlush>
                <div className="flex items-center justify-between gap-4 border-b border-border p-5">
                  <button
                    type="button"
                    aria-expanded={isOpen}
                    aria-controls={`flags-${categoryKey}`}
                    onClick={() => {
                      toggleCategory(categoryKey)
                    }}
                    className="-m-1 flex min-w-0 flex-1 items-center gap-2 rounded p-1 text-left"
                  >
                    <IconExpandMore
                      size={18}
                      className={cn(
                        'shrink-0 text-text-muted transition-transform',
                        !isOpen && '-rotate-90',
                      )}
                    />
                    <CardHeader
                      title={category.name}
                      description={
                        allOn
                          ? t('flags.categoryOn', { name: category.name })
                          : t('flags.categoryOff', { name: category.name })
                      }
                      as="h2"
                      className="min-w-0 flex-1"
                    />
                    <span data-numeric className="shrink-0 text-xs text-text-muted">
                      {category.flags.length}
                    </span>
                  </button>
                  <Button
                    variant="secondary"
                    size="sm"
                    isLoading={setCategory.isPending}
                    onClick={() => {
                      setCategory.mutate(
                        { categoryKey, isEnabled: !allOn },
                        {
                          onSuccess: () => {
                            toast.success(t('flags.updated'))
                          },
                          onError: (error) => {
                            toast.fromError(error, t('state.errorTitle'))
                          },
                        },
                      )
                    }}
                  >
                    {/* An action, not a state. Sitting beside "Everything in Worlds is on",
                        a button reading "Off" is genuinely ambiguous — is that the current
                        state or what pressing it does? */}
                    {allOn ? t('flags.turnAllOff') : t('flags.turnAllOn')}
                  </Button>
                </div>

                <ul id={`flags-${categoryKey}`} className="divide-y divide-border" hidden={!isOpen}>
                  {category.flags.map((flag) => (
                    <li key={flag.key} className="p-5">
                      <div className="flex items-start justify-between gap-4">
                        <Switch
                          label={flag.key}
                          description={flag.description}
                          isChecked={flag.isEnabled}
                          isDisabled={flag.isSystem}
                          disabledReason={flag.isSystem ? t('flags.systemHint') : undefined}
                          onChange={(next) => {
                            // Switching a feature OFF reaches everybody instantly, including
                            // people mid-session. Switching one on harms nobody, so only the
                            // destructive direction asks.
                            if (next) apply(flag, true)
                            else setConfirmingOff(flag)
                          }}
                        />
                        {flag.isSystem ? <Badge tone="info">{t('flags.badgeSystem')}</Badge> : null}
                      </div>

                      <div className="mt-3 ml-12">
                        <div className="flex items-center justify-between gap-3">
                          <p className="text-xs font-medium text-text-secondary">
                            {t('flags.overrides')}
                          </p>
                          <Button
                            variant="ghost"
                            size="sm"
                            icon={<IconAdd size={13} />}
                            onClick={() => {
                              setOverriding(flag)
                            }}
                          >
                            {t('flags.addOverride')}
                          </Button>
                        </div>
                        {flag.overrides.length === 0 ? (
                          <p className="mt-1 text-xs text-text-muted">{t('flags.noOverrides')}</p>
                        ) : (
                          <ul className="mt-1.5 flex flex-wrap gap-1.5">
                            {flag.overrides.map((override) => (
                              <li key={override.id}>
                                <span className="inline-flex items-center gap-1.5 rounded-full border border-border bg-surface-sunken py-0.5 pr-1 pl-2 text-xs">
                                  <span className="text-text">
                                    {override.roleName ?? override.userDisplayName ?? '—'}
                                  </span>
                                  <span
                                    className={
                                      override.isEnabled ? 'text-success' : 'text-text-muted'
                                    }
                                  >
                                    {override.isEnabled ? t('flags.on') : t('flags.off')}
                                  </span>
                                  <button
                                    type="button"
                                    aria-label={t('flags.removeOverride')}
                                    className="rounded p-0.5 text-text-muted hover:text-danger"
                                    onClick={() => {
                                      removeOverride.mutate(
                                        {
                                          key: flag.key,
                                          ...(override.roleId ? { roleId: override.roleId } : {}),
                                          ...(override.userId ? { userId: override.userId } : {}),
                                        },
                                        {
                                          onSuccess: () => {
                                            toast.success(t('flags.overrideRemoved'))
                                          },
                                          onError: (error) => {
                                            toast.fromError(error, t('state.errorTitle'))
                                          },
                                        },
                                      )
                                    }}
                                  >
                                    <IconClose size={11} />
                                  </button>
                                </span>
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                    </li>
                  ))}
                </ul>
              </Card>
            )
          })}
        </div>
      )}

      <FlagOverrideDialog
        flag={overriding}
        onClose={() => {
          setOverriding(null)
        }}
      />

      <ConfirmDialog
        isOpen={confirmingOff !== null}
        onClose={() => {
          setConfirmingOff(null)
        }}
        onConfirm={() => {
          if (confirmingOff) apply(confirmingOff, false)
        }}
        title={t('flags.toggleConfirm', { name: confirmingOff?.key ?? '' })}
        body={t('flags.toggleBody')}
        confirmLabel={t('flags.off')}
        cancelLabel={t('common.cancel')}
        isPending={setFlag.isPending}
      />
    </>
  )
}
